import { BIOMES } from "../content/biomes";
import { POI_DEFS, type PoiKind } from "../content/pois";
import type { BiomeId } from "../content/schemas";
import { CHUNK_SIZE_M } from "../simulation/constants";
import { createRng, hashSeed, rngInt, rngRange } from "../simulation/rng";
import { biomeAt, resolveDangerTier } from "./biomes";
import { chunkKey, worldToChunk } from "./coordinates";
import { sampleTerrain } from "./terrain";

export type ChunkPoi = {
  kind: PoiKind;
  localX: number;
  localY: number;
  radius: number;
};

/** Low-poly industrial prop placed in a chunk (render uses InstancedMesh). */
export type ChunkBuilding = {
  localX: number;
  localY: number;
  yaw: number;
  /** 0–6 industrial kits, 7 dead tree, 8 conifer. */
  variant: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  scale: number;
};

export type ChunkData = {
  key: string;
  cx: number;
  cy: number;
  biome: BiomeId;
  pois: ChunkPoi[];
  buildings: ChunkBuilding[];
  hazard: number;
  dangerTier: 1 | 2 | 3;
};

export function generateChunk(seed: number, cx: number, cy: number): ChunkData {
  const key = chunkKey(cx, cy);
  const biome = biomeAt(seed, cx, cy);
  const biomeDef = BIOMES[biome];
  const rng = createRng(hashSeed(seed, cx * 91_783 + cy * 47_927 + 13));
  const pois: ChunkPoi[] = [];
  const count = rngInt(rng, 0, 3);
  for (let i = 0; i < count; i += 1) {
    const roll = rng();
    const kind =
      roll < 0.35
        ? "scrap_field"
        : roll < 0.55
          ? "relay"
          : roll < 0.75
            ? "ambush"
            : roll < 0.92
              ? "cache"
              : "boss_arena";
    pois.push({
      kind,
      localX: rngRange(rng, 12, CHUNK_SIZE_M - 12),
      localY: rngRange(rng, 12, CHUNK_SIZE_M - 12),
      radius: POI_DEFS[kind].radius,
    });
  }

  // Hazard skewed by biome so Scrap Canyon tends hotter, Ash Band safer.
  const hazardBase = rngRange(rng, 0.15, 0.95);
  const hazard = Math.min(1, Math.max(0.05, hazardBase * (0.65 + biomeDef.dangerTier * 0.18)));

  const buildings = placeBuildings(
    rng,
    seed,
    cx,
    cy,
    biome,
    biomeDef.scrapDensity,
    biomeDef.dangerTier,
  );

  return {
    key,
    cx,
    cy,
    biome,
    pois,
    buildings,
    hazard,
    dangerTier: resolveDangerTier(biome, hazard),
  };
}

function placeBuildings(
  rng: () => number,
  seed: number,
  cx: number,
  cy: number,
  biome: BiomeId,
  scrapDensity: number,
  dangerTier: number,
): ChunkBuilding[] {
  const buildings: ChunkBuilding[] = [];
  const clusterRoll = rng();
  if (clusterRoll < 0.18) return buildings;

  const cityCluster = clusterRoll > 0.72;
  const wreckField = !cityCluster && clusterRoll < 0.38;
  const baseCount = cityCluster
    ? rngInt(rng, 8, 16)
    : wreckField
      ? rngInt(rng, 2, 5)
      : rngInt(rng, 1, 3 + Math.floor(scrapDensity + dangerTier * 0.4));
  const count = Math.min(18, Math.round(baseCount * (cityCluster ? 1 : scrapDensity)));
  const clusterX = rngRange(rng, 18, CHUNK_SIZE_M - 18);
  const clusterY = rngRange(rng, 18, CHUNK_SIZE_M - 18);
  const spread = cityCluster ? 20 : wreckField ? 28 : 32;

  for (let i = 0; i < count; i += 1) {
    const ox =
      cityCluster || wreckField
        ? rngRange(rng, -spread, spread)
        : rngRange(rng, 10, CHUNK_SIZE_M - 10);
    const oy =
      cityCluster || wreckField
        ? rngRange(rng, -spread, spread)
        : rngRange(rng, 10, CHUNK_SIZE_M - 10);
    const localX = Math.min(
      CHUNK_SIZE_M - 6,
      Math.max(6, (cityCluster || wreckField ? clusterX : 0) + ox),
    );
    const localY = Math.min(
      CHUNK_SIZE_M - 6,
      Math.max(6, (cityCluster || wreckField ? clusterY : 0) + oy),
    );
    const wx = cx * CHUNK_SIZE_M + localX;
    const wy = cy * CHUNK_SIZE_M + localY;
    const land = sampleTerrain(seed, wx, wy);
    if (land.water || land.height < 0.7 || Math.hypot(wx, wy) < 320) continue;
    const vRoll = rng();
    let variant: ChunkBuilding["variant"];
    if (wreckField) {
      variant = vRoll < 0.55 ? 5 : 6;
    } else if (cityCluster) {
      variant = vRoll < 0.28 ? 0 : vRoll < 0.55 ? 1 : vRoll < 0.72 ? 2 : vRoll < 0.88 ? 3 : 4;
    } else {
      variant =
        vRoll < 0.22
          ? 0
          : vRoll < 0.4
            ? 1
            : vRoll < 0.55
              ? 2
              : vRoll < 0.68
                ? 3
                : vRoll < 0.8
                  ? 4
                  : vRoll < 0.9
                    ? 5
                    : 6;
    }
    buildings.push({
      localX,
      localY,
      yaw: cityCluster ? rngInt(rng, 0, 4) * (Math.PI / 2) : rngRange(rng, 0, Math.PI * 2),
      variant,
      scale: rngRange(rng, 0.8, 1.3) * (variant === 0 ? 1.12 : variant === 5 ? 1.35 : 1),
    });
  }

  // Sparse vegetation is a separate, low profile landmark layer. It gives
  // wetter lowlands life without turning the chase camera into a canopy.
  const treeCount = rngInt(rng, 3, 10);
  for (let i = 0; i < treeCount; i += 1) {
    const localX = rngRange(rng, 8, CHUNK_SIZE_M - 8);
    const localY = rngRange(rng, 8, CHUNK_SIZE_M - 8);
    const wx = cx * CHUNK_SIZE_M + localX;
    const wy = cy * CHUNK_SIZE_M + localY;
    const land = sampleTerrain(seed, wx, wy);
    if (
      land.water ||
      land.height < 0.8 ||
      land.height > 12 ||
      land.moisture < 0.53 ||
      Math.hypot(wx, wy) < 320
    ) {
      continue;
    }
    buildings.push({
      localX,
      localY,
      yaw: rngRange(rng, 0, Math.PI * 2),
      variant: biome === "scrap_canyon" ? 7 : rng() < 0.32 ? 7 : 8,
      scale: rngRange(rng, 0.72, 1.12),
    });
  }
  return buildings;
}

export function activeChunkCoords(
  x: number,
  y: number,
  radius: number,
): Array<{ cx: number; cy: number }> {
  const origin = worldToChunk(x, y, CHUNK_SIZE_M);
  const out: Array<{ cx: number; cy: number }> = [];
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      out.push({ cx: origin.cx + dx, cy: origin.cy + dy });
    }
  }
  return out;
}

export class ChunkStreamer {
  private readonly cache = new Map<string, ChunkData>();
  /** Stable neighborhood while the player stays in the same chunk cell. */
  private lastCx = Number.NaN;
  private lastCy = Number.NaN;
  private lastRadius = -1;
  private lastResult: ChunkData[] = [];

  public constructor(private readonly seed: number) {}

  public ensureAround(x: number, y: number, radius: number): ChunkData[] {
    const origin = worldToChunk(x, y, CHUNK_SIZE_M);
    if (origin.cx === this.lastCx && origin.cy === this.lastCy && radius === this.lastRadius) {
      return this.lastResult;
    }

    const result: ChunkData[] = [];
    for (let dy = -radius; dy <= radius; dy += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) {
        const cx = origin.cx + dx;
        const cy = origin.cy + dy;
        const key = chunkKey(cx, cy);
        let chunk = this.cache.get(key);
        if (!chunk) {
          chunk = generateChunk(this.seed, cx, cy);
          this.cache.set(key, chunk);
        }
        result.push(chunk);
      }
    }
    this.lastCx = origin.cx;
    this.lastCy = origin.cy;
    this.lastRadius = radius;
    this.lastResult = result;
    return result;
  }

  public getCached(key: string): ChunkData | undefined {
    return this.cache.get(key);
  }

  public chunkAt(x: number, y: number): ChunkData {
    const { cx, cy } = worldToChunk(x, y, CHUNK_SIZE_M);
    const key = chunkKey(cx, cy);
    let chunk = this.cache.get(key);
    if (!chunk) {
      chunk = generateChunk(this.seed, cx, cy);
      this.cache.set(key, chunk);
    }
    return chunk;
  }
}
